import { useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client.js";

/**
 * FIX-011 / RE Persona Voice — Admin section (additive; a standalone component rendered by AdminPage, its own state, NO fetch on mount: everything loads
 * on an explicit admin action). Assigns an ElevenLabs voice id to a persona and creates its ONE-TIME voice sample. Talks only to the `/re/` endpoints
 * and the existing `GET /admin/workspaces/:tenantId/personas` list.
 */
interface WorkspaceOption {
  readonly tenantId: string;
  readonly name: string;
}

interface AdminPersona {
  readonly personaId: string;
  readonly displayName: string;
}

export interface PersonaVoiceView {
  readonly personaId: string;
  readonly registered: boolean;
  readonly voiceId: string | null;
  readonly voiceDisplayName: string | null;
  readonly status: "UNVERIFIED" | "VERIFIED" | "UNAVAILABLE" | null;
  readonly hasSample: boolean;
  readonly sampleDurationMs: number | null;
}

const ERROR_TEXT: Record<string, string> = {
  admin_only: "Only the platform admin can do this.",
  voice_conflict: "This persona already has a different voice assigned. It is never replaced silently.",
  voice_not_ready: "Assign a voice to this persona first (or the assigned voice is unavailable).",
  voice_sample_not_configured: "Voice sample creation is not configured on this deployment.",
  sample_invalid: "The generated sample was rejected (format or duration). Nothing was stored.",
  sample_storage_failed: "The sample could not be stored. Nothing was linked to the persona.",
  provider_failed: "The voice provider failed. No sample was stored.",
  provider_timeout: "The voice provider timed out. No sample was stored.",
  invalid_input: "The input is not valid.",
  csrf_invalid: "Your session expired. Reload the page and try again.",
};

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return (body?.error !== undefined ? ERROR_TEXT[body.error] : undefined) ?? body?.message ?? fallback;
  }
  return fallback;
}

function statusLabel(v: PersonaVoiceView): string {
  if (!v.registered) return "No voice assigned";
  if (v.status === "VERIFIED") return `Ready${v.sampleDurationMs !== null ? ` (${(v.sampleDurationMs / 1000).toFixed(1)} s sample)` : ""}`;
  if (v.status === "UNAVAILABLE") return "Voice unavailable";
  return "Voice assigned — sample not created";
}

export function PersonaVoiceSection({ workspaces, csrfToken }: { readonly workspaces: readonly WorkspaceOption[] | null; readonly csrfToken: string | null }) {
  const [tenantId, setTenantId] = useState("");
  const [personas, setPersonas] = useState<readonly AdminPersona[] | null>(null);
  const [voices, setVoices] = useState<Record<string, PersonaVoiceView>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [voiceIdInput, setVoiceIdInput] = useState<Record<string, string>>({});
  const [nameInput, setNameInput] = useState<Record<string, string>>({});
  const [textInput, setTextInput] = useState<Record<string, string>>({});

  const voiceUrl = (personaId: string) => `/re/${encodeURIComponent(tenantId)}/personas/${encodeURIComponent(personaId)}/voice`;

  async function load(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setPersonas(null);
    setVoices({});
    try {
      const result = await api.get<{ personas: AdminPersona[] }>(`/admin/workspaces/${encodeURIComponent(tenantId)}/personas`);
      setPersonas(result.personas);
      for (const persona of result.personas) {
        try {
          const view = await api.get<PersonaVoiceView>(voiceUrl(persona.personaId));
          setVoices((current) => ({ ...current, [persona.personaId]: view }));
        } catch {
          // One persona's status failing to load must not hide the others; its row simply shows no status.
        }
      }
    } catch (err) {
      setError(errorMessage(err, "Could not load personas for that workspace."));
    }
  }

  async function assign(personaId: string): Promise<void> {
    setError(null);
    setBusy(personaId);
    try {
      const view = await api.post<PersonaVoiceView>(voiceUrl(personaId), csrfToken, { voiceId: (voiceIdInput[personaId] ?? "").trim(), voiceDisplayName: (nameInput[personaId] ?? "").trim() });
      setVoices((current) => ({ ...current, [personaId]: view }));
    } catch (err) {
      setError(errorMessage(err, "Could not assign the voice."));
    } finally {
      setBusy(null);
    }
  }

  async function createSample(personaId: string): Promise<void> {
    setError(null);
    if (!window.confirm("This makes ONE paid ElevenLabs call and stores the voice sample permanently for this persona. Continue?")) return;
    setBusy(personaId);
    try {
      const view = await api.post<PersonaVoiceView>(`${voiceUrl(personaId)}/sample`, csrfToken, { text: (textInput[personaId] ?? "").trim() });
      setVoices((current) => ({ ...current, [personaId]: view }));
    } catch (err) {
      setError(errorMessage(err, "Could not create the voice sample."));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card" data-testid="persona-voice-section">
      <h2>Persona voice</h2>
      <p className="muted">Assign an ElevenLabs voice to a persona and create its one-time voice sample (stored once; never regenerated per video).</p>
      <form onSubmit={(event) => void load(event)}>
        <div className="field">
          <label htmlFor="personaVoiceWorkspaceId">Workspace</label>
          <select id="personaVoiceWorkspaceId" required value={tenantId} onChange={(event) => setTenantId(event.target.value)}>
            <option value="">Select…</option>
            {workspaces?.map((w) => (
              <option key={w.tenantId} value={w.tenantId}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" className="primary" data-testid="persona-voice-load">
          Load personas
        </button>
      </form>
      {error !== null && (
        <div className="error-banner" data-testid="persona-voice-error">
          {error}
        </div>
      )}
      {personas !== null && (
        <table>
          <thead>
            <tr>
              <th>Persona</th>
              <th>Voice</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {personas.map((p) => {
              const v = voices[p.personaId];
              return (
                <tr key={p.personaId} data-testid={`persona-voice-row-${p.personaId}`}>
                  <td>{p.displayName}</td>
                  <td>{v?.registered === true ? `${v.voiceDisplayName ?? ""} (${v.voiceId ?? ""})` : <span className="muted">—</span>}</td>
                  <td data-testid={`persona-voice-status-${p.personaId}`}>{v === undefined ? <span className="muted">unknown</span> : statusLabel(v)}</td>
                  <td>
                    {v !== undefined && !v.registered && (
                      <div>
                        <input aria-label={`Voice id for ${p.displayName}`} placeholder="ElevenLabs voice id" value={voiceIdInput[p.personaId] ?? ""} onChange={(e) => setVoiceIdInput((c) => ({ ...c, [p.personaId]: e.target.value }))} />
                        <input aria-label={`Voice name for ${p.displayName}`} placeholder="Voice name" value={nameInput[p.personaId] ?? ""} onChange={(e) => setNameInput((c) => ({ ...c, [p.personaId]: e.target.value }))} />
                        <button type="button" data-testid={`persona-voice-assign-${p.personaId}`} disabled={busy !== null} onClick={() => void assign(p.personaId)}>
                          Assign voice
                        </button>
                      </div>
                    )}
                    {v !== undefined && v.registered && !v.hasSample && v.status !== "UNAVAILABLE" && (
                      <div>
                        <textarea aria-label={`Sample text for ${p.displayName}`} rows={3} placeholder="Neutral sample text (20–1000 characters, about 8–10 seconds when spoken)" value={textInput[p.personaId] ?? ""} onChange={(e) => setTextInput((c) => ({ ...c, [p.personaId]: e.target.value }))} />
                        <button type="button" data-testid={`persona-voice-sample-${p.personaId}`} disabled={busy !== null} onClick={() => void createSample(p.personaId)}>
                          Create sample (one paid call)
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {personas.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  No personas in this workspace.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
